import { ApiError } from '../client';
import type { RecipientChange } from '../endpoints/changeDeliveryRecipient';
import {
  currentRecipientIdInMock,
  currentStatusInMock,
  getChangeRecipientMockError,
  markDeliveryRecipientChangedInMock,
} from './deliveryDetail';

// `stored` and `expired`, lowercased before the test: the wire is lowercase and the guard is fail
// closed, so an unrecognized state is not reassignable. Spelled here rather than imported — the
// canonical constants live in the care app, and a package does not reach into an app.
const REASSIGNABLE_STATES = new Set(['stored', 'expired']);

// Mock of the change-recipient WRITE: simulate latency, then either reject with an injected backend
// error code (dev/test hook, so each per-code sentence is reachable) or hand the «Entrega» to the
// new titular in the shared detail mock — which also revokes its authorized pickups, so a follow-up
// fetchDeliveryDetailMock returns both halves of the transaction at once.
//
// The guards run in the order the real orchestrator runs them: everything about the «Entrega»
// first, the destination account last. That order is the guarantee that a REJECTED reassignment
// revokes nobody, and it is cheap to preserve here because it costs only the sequence.
export async function changeDeliveryRecipientMock(
  id: string,
  change: RecipientChange,
): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  const injected = getChangeRecipientMockError();
  if (injected !== null) {
    throw new ApiError(409, injected, { code: injected }, `change recipient failed: ${injected}`);
  }

  if (!REASSIGNABLE_STATES.has(currentStatusInMock(id).toLowerCase())) {
    throw new ApiError(
      409,
      'delivery_state_conflict',
      { code: 'delivery_state_conflict' },
      'change recipient failed: delivery is not stored or expired',
    );
  }

  // The compare-and-set. A `null` expectation is a claim like any other — «the card showed no
  // titular» — so it is compared, never treated as «skip the check».
  const current = currentRecipientIdInMock(id);
  if (current !== change.expectedCurrentUserId) {
    throw new ApiError(
      409,
      'delivery_recipient_conflict',
      { code: 'delivery_recipient_conflict' },
      'change recipient failed: the titular changed since it was read',
    );
  }

  if (change.userId === current) {
    throw new ApiError(
      409,
      'delivery_recipient_is_current',
      { code: 'delivery_recipient_is_current' },
      'change recipient failed: that account already holds the parcel',
    );
  }

  markDeliveryRecipientChangedInMock(id, change.userId);
}
